import { Component, Inject, OnInit, OnDestroy, NgZone } from '@angular/core';
import { Router} from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { API_BASE_URL } from '../../../environments/environment';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { AddressLabelPositionDialogComponent } from '../address-label-position-dialog/address-label-position-dialog.component';
import { Subscription, interval, timer } from 'rxjs';
import { exhaustMap, takeWhile, tap, retry } from 'rxjs/operators';

export interface DialogData {
  selection: any;
  url: String;
}

interface PrintJobStatus {
  // バックエンドのJobStatus.Status（Java enum）はJacksonのデフォルト設定で大文字のままシリアライズされる
  status: 'RUNNING' | 'DONE' | 'ERROR';
  progress: number;
  result?: string;
  errorMessage?: string;
}

@Component({
  selector: 'app-address-print-dialog',
  templateUrl: './address-print-dialog.component.html',
  styleUrls: ['./address-print-dialog.component.less'],
})

export class AddressPrintDialogComponent implements OnInit, OnDestroy {

  private _nowLoading = false;

  // 印刷中はバックドロップクリック・ESCで閉じられないようにする（disableCloseと連動）
  get nowLoading(): boolean {
    return this._nowLoading;
  }
  set nowLoading(value: boolean) {
    this._nowLoading = value;
    this.dialogRef.disableClose = value;
  }

  public headers = new HttpHeaders({
    "X-LoginAccessKey": localStorage.getItem("accessKey")
  });

  public chk = new Array(30).fill(true);

  // httpヘッダー
  public httpOptions;

  // シールのサイズ番号（Ａ４=1、長形３号=2 に続く番号）
  private readonly ADDRESS_LABEL_SIZE = 3;

  // 印刷処理の進捗（0〜100）
  printProgress = 0;

  private startSub: Subscription;
  private pollingSub: Subscription;

  constructor(private http: HttpClient, private router: Router,
    @Inject(MAT_DIALOG_DATA) private data: DialogData,
    private dialogRef: MatDialogRef<AddressPrintDialogComponent>,
    private ngZone: NgZone,
    private dialog: MatDialog) { }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.stopRequests(); // ダイアログが閉じられた時に通信を止め忘れない
  }

  /**
   * キャンセル クリック
   * 通信がハングした場合等の脱出手段として、disableCloseに関わらず強制的に閉じる。
   */
  cancelPrint() {
    this.stopRequests();
    this.nowLoading = false;
    this.dialogRef.close(false);
  }

  /**
   * 進行中の通信（開始POST・ポーリング）を全て中断する
   */
  private stopRequests() {
    if (this.startSub) {
      this.startSub.unsubscribe();
    }
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
    }
  }

  // 合計件数：呼び出し元から渡された選択件数からそのまま算出する
  get totalCount(): number {
    return this.data.selection ? this.data.selection.length : 0;
  }

  // 処理済み件数（概算）：progress(%)と合計件数から逆算する
  get printedCount(): number {
    return Math.round((this.printProgress / 100) * this.totalCount);
  }

  goTo(comp, param){
    this.ngZone.run(()=>{
    this.router.navigate([comp, param])
    });
  }
  
  /**
   * シール クリック
   * 印刷開始位置ダイアログを開く
   */
  openAddressLabelPositionDialog() {
    const ref = this.dialog.open(AddressLabelPositionDialogComponent, { autoFocus: false });
    ref.afterClosed().subscribe(startIndex => {
      if (startIndex === null || startIndex === undefined) {
        // キャンセル
        return;
      }
      // バックエンドは0始まりのため、選択インデックス（0〜11）をそのまま渡す
      this.clickPrint(this.ADDRESS_LABEL_SIZE, startIndex);
    });
  }

  /**
   * 印刷 クリック
   * 処理開始のみ行い、以降はポーリングで状態確認する。
   * @param size     封筒/シールのサイズ番号
   * @param startPos シール印刷時の開始位置（0〜11）。それ以外は未指定
   */
  clickPrint(size, startPos?: number) {
    if (this.nowLoading) {
      return;
    }
    this.nowLoading = true;
    this.printProgress = 0;

    let startUrl = API_BASE_URL + this.data.url + size + '/start';
    if (startPos !== null && startPos !== undefined) {
      startUrl += '?startPos=' + startPos;
    }

    this.startSub = this.http.post<{ jobId: string }>(startUrl, this.data.selection, { headers: this.headers })
      .subscribe(res => {
        this.pollPrintStatus(res.jobId);
      }, error => {
        this.nowLoading = false;
        alert("セッションが切れています");
        this.dialogRef.close(false);
        this.router.navigate(['/']);
      });
  }

  /**
   * ジョブの状態を完了/エラーになるまでポーリングする
   */
  private pollPrintStatus(jobId: string) {
    // 状態確認APIはリソースごとに存在するため、this.data.urlをそのまま流用する
    const statusUrl = API_BASE_URL + this.data.url + 'status/' + jobId;

    this.pollingSub = interval(3000).pipe(
      // exhaustMap: 前回のstatus取得中は新しいintervalを無視する（read-once方式のジョブが未受信のまま破棄されるのを防ぐ）
      // retry: 一時的な通信エラーでポーリング全体を打ち切らないよう、1回のstatus確認につき最大2回まで自動リトライする
      exhaustMap(() => this.http.get<PrintJobStatus>(statusUrl, { headers: this.headers }).pipe(retry(2))),
      tap(res => {
        this.printProgress = res.progress;
        if (res.status === 'DONE') {
          if (!res.result) {
            this.nowLoading = false;
            alert("印刷データの取得に失敗しました");
            this.dialogRef.close(false);
            return;
          }
          // 100%表示を描画させてから閉じるため少し待つ
          this.pollingSub = timer(500).subscribe(() => {
            this.downloadPdf(res.result);
            this.nowLoading = false;
            this.dialogRef.close(true); // 成功を呼び出し元に伝える
          });
        } else if (res.status === 'ERROR') {
          this.nowLoading = false;
          alert(res.errorMessage || "印刷処理に失敗しました");
          this.dialogRef.close(false);
        } else if (res.status !== 'RUNNING') {
          // RUNNING/DONE/ERROR以外の想定外の値が返ってきた場合もフリーズさせず閉じる
          this.nowLoading = false;
          alert("印刷処理に失敗しました");
          this.dialogRef.close(false);
        }
      }),
      takeWhile(res => res.status === 'RUNNING')
    ).subscribe(() => {}, error => {
      this.nowLoading = false;
      alert("セッションが切れています");
      this.dialogRef.close(false);
      this.router.navigate(['/']);
    });
  }

  /**
   * Base64とMIMEコンテンツタイプからBlobオブジェクトを作成する。
   * 
   * @param base64
   */
  downloadPdf (base64) {
    var mime_ctype = "application/pdf";
    var blob = this.toBlob(base64, mime_ctype);
    window.open(window.URL.createObjectURL(blob), '_blank')      
  }
   
  /**
   * Base64とMIMEコンテンツタイプからBlobオブジェクトを作成する。
   * 日本語対応。
   * 
   * @param base64 
   * @param mime_ctype MIMEコンテンツタイプ
   * @returns Blob
   */
  toBlob(base64, mime_ctype) {
    // 日本語の文字化けに対処するためBOMを作成する。
    var bom = new Uint8Array([0xEF, 0xBB, 0xBF]);

    var bin = atob(base64.replace(/^.*,/, ''));
    var buffer = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) {
        buffer[i] = bin.charCodeAt(i);
    }
    // Blobを作成
    try {
        var blob = new Blob([bom, buffer.buffer], {
            type: mime_ctype,
        });
    } catch (e) {
        return null;
    }
    return blob;
  }  
}


